import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton, Stack, TextField, MenuItem } from "@mui/material";
import {
  ArrowBackIosNew,
  Favorite,
  Repeat,
  ChatBubbleOutline,
  IosShare,
  BookmarkBorder,
  Group,
  Campaign,
} from "@mui/icons-material";
import { auth, db } from "./firebase";
import { collection, query, where, onSnapshot, doc } from "firebase/firestore";
import { useTranslatedDictionary } from "./hooks/useTranslatedDictionary";

const STATS_TEXT = {
  es: {
    title: "Estadísticas",
    subtitle: "Resumen total de interacción y promoción",
    chartTitle: "Gráfico tipo trading",
    chartHint: "Eje Y: valor | Eje X: fecha",
    metric: "Métrica",
    fromDate: "Desde",
    toDate: "Hasta",
    chartNoData: "Sin datos en el rango seleccionado",
    likes: "Me gusta",
    reposts: "Republicado",
    commented: "Comentado",
    shared: "Compartido",
    saved: "Guardado",
    followers: "Seguidores",
    promoted: "Promocionar",
  },
  en: {
    title: "Statistics",
    subtitle: "Total interaction and promotion summary",
    chartTitle: "Trading-style chart",
    chartHint: "Y axis: value | X axis: date",
    metric: "Metric",
    fromDate: "From",
    toDate: "To",
    chartNoData: "No data in selected range",
    likes: "Likes",
    reposts: "Reposted",
    commented: "Commented",
    shared: "Shared",
    saved: "Saved",
    followers: "Followers",
    promoted: "Promoted",
  },
};

const Estadisticas = ({ onBack, language = "es", themeMode = "oscuro" }) => {
  const user = auth.currentUser;
  const todayIso = new Date().toISOString().slice(0, 10);
  const defaultFrom = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  const [engagementStats, setEngagementStats] = useState({
    likes: 0,
    reposts: 0,
    commented: 0,
    shared: 0,
    saved: 0,
    followers: 0,
    promoted: 0,
  });
  const [postsData, setPostsData] = useState([]);
  const [adsData, setAdsData] = useState([]);
  const [followersTotal, setFollowersTotal] = useState(0);
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(todayIso);
  const [selectedMetric, setSelectedMetric] = useState("likes");

  const t = useTranslatedDictionary(STATS_TEXT, language);
  const isLightTheme = themeMode === "claro";
  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;

  useEffect(() => {
    if (!user) return;

    const postsQuery = query(collection(db, "posts"), where("uid", "==", user.uid));
    const unsubscribePosts = onSnapshot(postsQuery, (snapshot) => {
      setPostsData(snapshot.docs.map((postDoc) => postDoc.data() || {}));
    });

    const unsubscribeUser = onSnapshot(doc(db, "users", user.uid), (snapshot) => {
      if (!snapshot.exists()) return;
      const data = snapshot.data() || {};
      const nextFollowersTotal = Number(data.followersCount) || (Array.isArray(data.followers) ? data.followers.length : 0);
      setFollowersTotal(nextFollowersTotal);
    });

    const adsQuery = query(collection(db, "ads"), where("ownerId", "==", user.uid));
    const unsubscribeAds = onSnapshot(adsQuery, (snapshot) => {
      setAdsData(snapshot.docs.map((adDoc) => adDoc.data() || {}));
    });

    return () => {
      unsubscribePosts();
      unsubscribeUser();
      unsubscribeAds();
    };
  }, [user]);

  useEffect(() => {
    const totals = {
      likes: 0,
      reposts: 0,
      commented: 0,
      shared: 0,
      saved: 0,
      followers: followersTotal,
      promoted: adsData.length,
    };

    postsData.forEach((post) => {
      totals.likes += Array.isArray(post.likes) ? post.likes.length : 0;
      totals.reposts += Number(post.repostCount) || 0;
      totals.commented += Number(post.commentsCount) || 0;
      totals.shared += Number(post.sentCount) || 0;
      totals.saved += Array.isArray(post.savedBy) ? post.savedBy.length : 0;
    });

    setEngagementStats(totals);
  }, [postsData, adsData.length, followersTotal]);

  const statsItems = [
    { key: "likes", icon: Favorite, color: "#ff6b8a" },
    { key: "reposts", icon: Repeat, color: "#6ee7ff" },
    { key: "commented", icon: ChatBubbleOutline, color: "#ffd166" },
    { key: "shared", icon: IosShare, color: "#9ee37d" },
    { key: "saved", icon: BookmarkBorder, color: "#c6a0ff" },
    { key: "followers", icon: Group, color: "#66c2ff" },
    { key: "promoted", icon: Campaign, color: "#00d4ff" },
  ];

  const toIsoDate = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value.slice(0, 10);
    if (value?.toDate) return value.toDate().toISOString().slice(0, 10);
    if (value instanceof Date) return value.toISOString().slice(0, 10);
    return "";
  };

  const buildDateRange = (startIso, endIso) => {
    if (!startIso || !endIso || startIso > endIso) return [];
    const result = [];
    const current = new Date(`${startIso}T00:00:00`);
    const end = new Date(`${endIso}T00:00:00`);
    while (current <= end) {
      result.push(current.toISOString().slice(0, 10));
      current.setDate(current.getDate() + 1);
      if (result.length > 62) break;
    }
    return result;
  };

  const dateRange = buildDateRange(fromDate, toDate);

  const seriesByMetric = statsItems.reduce((acc, item) => {
    const dateMap = {};
    dateRange.forEach((date) => {
      dateMap[date] = 0;
    });
    acc[item.key] = dateMap;
    return acc;
  }, {});

  postsData.forEach((post) => {
    const date = toIsoDate(post.timestamp);
    if (!date || !seriesByMetric.likes?.hasOwnProperty(date)) return;
    seriesByMetric.likes[date] += Array.isArray(post.likes) ? post.likes.length : 0;
    seriesByMetric.reposts[date] += Number(post.repostCount) || 0;
    seriesByMetric.commented[date] += Number(post.commentsCount) || 0;
    seriesByMetric.shared[date] += Number(post.sentCount) || 0;
    seriesByMetric.saved[date] += Array.isArray(post.savedBy) ? post.savedBy.length : 0;
  });

  adsData.forEach((ad) => {
    const date = toIsoDate(ad.createdAt);
    if (!date || !seriesByMetric.promoted?.hasOwnProperty(date)) return;
    seriesByMetric.promoted[date] += 1;
  });

  if (dateRange.length > 0) {
    seriesByMetric.followers[dateRange[dateRange.length - 1]] = followersTotal;
  }

  const chartSeries = dateRange.map((date, index) => ({
    index,
    date,
    value: Number(seriesByMetric[selectedMetric]?.[date]) || 0,
  }));
  const maxY = Math.max(1, ...chartSeries.map((point) => point.value));
  const chartWidth = 820;
  const chartHeight = 260;
  const chartPadding = { top: 16, right: 16, bottom: 34, left: 42 };
  const plotWidth = chartWidth - chartPadding.left - chartPadding.right;
  const plotHeight = chartHeight - chartPadding.top - chartPadding.bottom;

  const getX = (pointIndex) => {
    if (chartSeries.length <= 1) return chartPadding.left + plotWidth / 2;
    return chartPadding.left + (pointIndex * plotWidth) / (chartSeries.length - 1);
  };

  const getY = (value) => chartPadding.top + plotHeight - (value / maxY) * plotHeight;

  const linePath = chartSeries
    .map((point, idx) => `${idx === 0 ? "M" : "L"}${getX(point.index)} ${getY(point.value)}`)
    .join(" ");

  const areaPath = chartSeries.length
    ? `M${getX(chartSeries[0].index)} ${chartPadding.top + plotHeight} ${chartSeries
      .map((point) => `L${getX(point.index)} ${getY(point.value)}`)
      .join(" ")} L${getX(chartSeries[chartSeries.length - 1].index)} ${chartPadding.top + plotHeight} Z`
    : "";

  const yTicks = 5;
  const xLabelStep = Math.max(1, Math.ceil(chartSeries.length / 6));

  return (
    <Box
      sx={{
        color: onSurface(1),
        minHeight: "100dvh",
        width: "100%",
        pb: { xs: 11, sm: 10 },
        background: isLightTheme
          ? "radial-gradient(circle at top, rgba(0,150,180,0.08) 0%, #ffffff 40%, #f2f5f8 100%)"
          : "radial-gradient(circle at top, rgba(0, 212, 255, 0.14) 0%, rgba(0,0,0,0.95) 32%, #000 100%)",
      }}
    >
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: 0,
          bgcolor: isLightTheme ? "rgba(255,255,255,0.82)" : "rgba(0,0,0,0.82)",
          backdropFilter: "blur(10px)",
          zIndex: 10,
        }}
      >
        <IconButton onClick={onBack} sx={{ color: onSurface(1) }}>
          <ArrowBackIosNew />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          {t("title")}
        </Typography>
        <Box sx={{ width: 40 }} />
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3 }, py: 3, maxWidth: "600px", mx: "auto" }}>
        <Box
          sx={{
            bgcolor: "rgba(0,212,255,0.05)",
            border: "1px solid rgba(0,212,255,0.2)",
            borderRadius: "14px",
            px: { xs: 1.6, sm: 2 },
            py: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, mb: 0.35 }}>{t("title")}</Typography>
          <Typography variant="caption" sx={{ color: onSurface(0.6), display: "block", mb: 1.6 }}>
            {t("subtitle")}
          </Typography>

          <Box
            sx={{
              mb: 1.4,
              px: 1.2,
              py: 1.2,
              borderRadius: "10px",
              border: `1px solid ${onSurface(0.12)}`,
              bgcolor: onSurface(0.03),
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.3 }}>
              {t("chartTitle")}
            </Typography>
            <Typography variant="caption" sx={{ color: onSurface(0.6), display: "block", mb: 1.2 }}>
              {t("chartHint")}
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1.2 }}>
              <TextField
                select
                label={t("metric")}
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
                size="small"
                sx={{
                  minWidth: 170,
                  "& .MuiOutlinedInput-root": {
                    color: onSurface(1),
                    "& fieldset": { borderColor: onSurface(0.2) },
                    "&:hover fieldset": { borderColor: onSurface(0.35) },
                    "&.Mui-focused fieldset": { borderColor: "#00d4ff" },
                  },
                  "& .MuiInputLabel-root": { color: onSurface(0.65) },
                }}
              >
                {statsItems.map((item) => (
                  <MenuItem key={`metric-${item.key}`} value={item.key}>
                    {t(item.key)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                type="date"
                label={t("fromDate")}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{
                  minWidth: 160,
                  "& .MuiOutlinedInput-root": {
                    color: onSurface(1),
                    "& fieldset": { borderColor: onSurface(0.2) },
                    "&:hover fieldset": { borderColor: onSurface(0.35) },
                    "&.Mui-focused fieldset": { borderColor: "#00d4ff" },
                  },
                  "& .MuiInputLabel-root": { color: onSurface(0.65) },
                }}
              />
              <TextField
                type="date"
                label={t("toDate")}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{
                  minWidth: 160,
                  "& .MuiOutlinedInput-root": {
                    color: onSurface(1),
                    "& fieldset": { borderColor: onSurface(0.2) },
                    "&:hover fieldset": { borderColor: onSurface(0.35) },
                    "&.Mui-focused fieldset": { borderColor: "#00d4ff" },
                  },
                  "& .MuiInputLabel-root": { color: onSurface(0.65) },
                }}
              />
            </Stack>

            {dateRange.length === 0 ? (
              <Typography variant="caption" sx={{ color: onSurface(0.65) }}>
                {t("chartNoData")}
              </Typography>
            ) : (
              <Box sx={{ overflowX: "auto", pb: 0.4 }}>
                <Box sx={{ minWidth: chartWidth }}>
                  <svg width="100%" viewBox={`0 0 ${chartWidth} ${chartHeight}`} role="img" aria-label="trade-chart">
                    <defs>
                      <linearGradient id="tradeArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgba(0,212,255,0.55)" />
                        <stop offset="100%" stopColor="rgba(0,212,255,0.06)" />
                      </linearGradient>
                    </defs>

                    {Array.from({ length: yTicks + 1 }).map((_, i) => {
                      const y = chartPadding.top + (i * plotHeight) / yTicks;
                      return (
                        <g key={`grid-y-${i}`}>
                          <line
                            x1={chartPadding.left}
                            y1={y}
                            x2={chartPadding.left + plotWidth}
                            y2={y}
                            stroke={onSurface(0.12)}
                            strokeDasharray="3 3"
                          />
                          <text
                            x={chartPadding.left - 8}
                            y={y + 3}
                            textAnchor="end"
                            fill={onSurface(0.68)}
                            fontSize="10"
                          >
                            {Math.round(maxY - (maxY * i) / yTicks)}
                          </text>
                        </g>
                      );
                    })}

                    <line
                      x1={chartPadding.left}
                      y1={chartPadding.top + plotHeight}
                      x2={chartPadding.left + plotWidth}
                      y2={chartPadding.top + plotHeight}
                      stroke={onSurface(0.22)}
                    />

                    {chartSeries.map((point, idx) => {
                      if (idx % xLabelStep !== 0 && idx !== chartSeries.length - 1) return null;
                      return (
                        <text
                          key={`x-label-${point.date}`}
                          x={getX(point.index)}
                          y={chartPadding.top + plotHeight + 16}
                          textAnchor="middle"
                          fill={onSurface(0.7)}
                          fontSize="10"
                        >
                          {point.date.slice(5)}
                        </text>
                      );
                    })}

                    {areaPath ? <path d={areaPath} fill="url(#tradeArea)" /> : null}
                    {linePath ? (
                      <path d={linePath} fill="none" stroke="#00d4ff" strokeWidth="2.2" />
                    ) : null}

                    {chartSeries.map((point) => (
                      <circle
                        key={`dot-${point.date}`}
                        cx={getX(point.index)}
                        cy={getY(point.value)}
                        r="2.2"
                        fill="#00e5ff"
                      />
                    ))}
                  </svg>
                </Box>
              </Box>
            )}
          </Box>

          <Stack spacing={1}>
            {statsItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <Box
                  key={item.key}
                  sx={{
                    px: 1.3,
                    py: 1.1,
                    borderRadius: "10px",
                    border: `1px solid ${onSurface(0.12)}`,
                    bgcolor: onSurface(0.03),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack direction="row" spacing={0.9} alignItems="center">
                    <IconComponent sx={{ fontSize: 18, color: item.color }} />
                    <Typography variant="body2" sx={{ color: onSurface(0.82), fontWeight: 600 }}>
                      {t(item.key)}
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>
                    {engagementStats[item.key] || 0}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default Estadisticas;